import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import './MemberEventRequirements.css';
import { translateError } from '../utils/errorMessages';
import { downloadRequirementFile } from '../utils/requirementFiles';

interface RequirementType {
  _id: string;
  name: string;
  code: string;
  description?: string;
  inputType: 'boolean' | 'text' | 'file' | 'date' | 'select';
  options?: string[];
  templateFile?: string;
  templateFileName?: string;
  isSystem: boolean;
}

interface MemberRequirement {
  _id: string;
  requirementType: RequirementType;
  status: 'pending' | 'submitted' | 'approved' | 'rejected';
  value?: string;
  submittedAt?: string;
  reviewedBy?: { _id: string; name: string };
  reviewedAt?: string;
  notes?: string;
}

interface EventRequirement {
  _id: string;
  requirementType: RequirementType;
  isRequired: boolean;
  dueDate?: string;
  description?: string;
}

interface MemberEventRequirementsProps {
  eventId: string;
  memberId: string;
  memberName?: string;
  onUpdate?: () => void;
}

const MemberEventRequirements: React.FC<MemberEventRequirementsProps> = ({
  eventId,
  memberId,
  memberName,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [eventRequirements, setEventRequirements] = useState<EventRequirement[]>([]);
  const [memberRequirements, setMemberRequirements] = useState<MemberRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  // Per-requirement errors (keyed by requirement type ID)
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});

  // Form state for each requirement
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  // Pending file uploads (file objects waiting to be uploaded on submit)
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});

  const fetchRequirements = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);

      // This component is rendered only from member-facing pages today, so the
      // member-owned requirement calls go to /api/my. The event-requirement
      // DEFINITIONS list is also fetched via the member-safe /api/my route
      // (admin route 403s for members). Branch by isAdmin so the component stays
      // safe to reuse on admin surfaces.
      const [eventReqResponse, memberReqResponse] = await Promise.all([
        isAdmin
          ? apiClient.get<EventRequirement[]>(`/api/requirements/events/${eventId}/requirements`)
          : api.my.eventRequirements.list(eventId),
        api.my.memberRequirements.list(memberId, eventId),
      ]);

      const memberReqData = memberReqResponse.data as MemberRequirement[];
      setEventRequirements(eventReqResponse.data);
      setMemberRequirements(memberReqData);

      // Initialize form values from existing submissions
      const initialValues: Record<string, string> = {};
      memberReqData.forEach(mr => {
        if (mr.value) {
          initialValues[mr.requirementType._id] = mr.value;
        }
      });
      setFormValues(initialValues);
    } catch (err: any) {
      setLoadError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [eventId, memberId, isAdmin]);

  useEffect(() => {
    if (eventId && memberId) {
      fetchRequirements();
    }
  }, [eventId, memberId, fetchRequirements]);

  const getMemberRequirement = (requirementTypeId: string): MemberRequirement | undefined => {
    return memberRequirements.find(mr => mr.requirementType._id === requirementTypeId);
  };

  const getStatusBadgeClass = (status?: string): string => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'submitted':
        return 'status-submitted';
      case 'rejected':
        return 'status-rejected';
      case 'pending':
      default:
        return 'status-pending';
    }
  };

  const handleInputChange = (requirementTypeId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [requirementTypeId]: value,
    }));
  };

  const handleSubmit = async (requirementTypeId: string) => {
    try {
      setSubmitting(requirementTypeId);
      // Clear previous error for this requirement
      setSubmitErrors(prev => {
        const updated = { ...prev };
        delete updated[requirementTypeId];
        return updated;
      });

      let value = formValues[requirementTypeId] || '';

      // If there's a pending file, upload it first
      const pendingFile = pendingFiles[requirementTypeId];
      if (pendingFile) {
        const formData = new FormData();
        formData.append('file', pendingFile);

        const uploadResponse = await api.my.memberRequirements.upload(memberId, formData);

        // Use the uploaded file URL as the value
        value = (uploadResponse.data as { fileUrl: string; fileName: string }).fileUrl;

        // Clear the pending file on success
        setPendingFiles(prev => {
          const updated = { ...prev };
          delete updated[requirementTypeId];
          return updated;
        });
      }

      await api.my.memberRequirements.submit(memberId, {
        requirementTypeId,
        eventId,
        value,
      });

      // Clear form value on success
      setFormValues(prev => {
        const updated = { ...prev };
        delete updated[requirementTypeId];
        return updated;
      });

      await fetchRequirements();
      if (onUpdate) onUpdate();
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      // Set per-requirement error
      setSubmitErrors(prev => ({
        ...prev,
        [requirementTypeId]: errorMessage,
      }));
      // Clear pending file on error so user can try again
      setPendingFiles(prev => {
        const updated = { ...prev };
        delete updated[requirementTypeId];
        return updated;
      });
      // Clear the form value for file inputs so user can re-select
      const reqType = eventRequirements.find(r => r.requirementType._id === requirementTypeId);
      if (reqType?.requirementType.inputType === 'file') {
        setFormValues(prev => {
          const updated = { ...prev };
          delete updated[requirementTypeId];
          return updated;
        });
      }
    } finally {
      setSubmitting(null);
    }
  };

  // Submissions are behind an authenticated route now, so a plain href to the
  // stored /uploads path no longer resolves — fetch the bytes with the token.
  const handleDownload = async (requirementId: string, reqTypeId: string) => {
    try {
      await downloadRequirementFile(memberId, requirementId, !isAdmin);
    } catch (err) {
      setSubmitErrors(prev => ({ ...prev, [reqTypeId]: translateError(err, t) }));
    }
  };

  const renderInputField = (eventReq: EventRequirement, memberReq?: MemberRequirement) => {
    const reqType = eventReq.requirementType;
    const reqTypeId = reqType._id;
    const currentValue = formValues[reqTypeId] || '';
    const isDisabled = memberReq?.status === 'approved' || submitting === reqTypeId;

    switch (reqType.inputType) {
      case 'boolean':
        return (
          <div className="requirement-input-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={currentValue === 'true'}
                onChange={(e) => handleInputChange(reqTypeId, e.target.checked ? 'true' : 'false')}
                disabled={isDisabled}
              />
              <span>{t('requirements.confirm')}</span>
            </label>
          </div>
        );

      case 'text':
        return (
          <div className="requirement-input-group">
            <textarea
              value={currentValue}
              onChange={(e) => handleInputChange(reqTypeId, e.target.value)}
              placeholder={t('requirements.enterText')}
              disabled={isDisabled}
              rows={3}
            />
          </div>
        );

      case 'file':
        const pendingFile = pendingFiles[reqTypeId];
        const hasUploadedFile = currentValue && currentValue.startsWith('/');
        return (
          <div className="requirement-input-group">
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // Store the file object for upload on submit
                  setPendingFiles(prev => ({ ...prev, [reqTypeId]: file }));
                  // Store filename for display
                  handleInputChange(reqTypeId, file.name);
                }
              }}
              disabled={isDisabled}
            />
            {pendingFile && (
              <span className="file-name">{t('requirements.fileSelected')}: {pendingFile.name}</span>
            )}
            {hasUploadedFile && !pendingFile && memberReq && (
              <a
                href="#"
                className="uploaded-file-link"
                onClick={(e) => { e.preventDefault(); handleDownload(memberReq._id, reqTypeId); }}
              >
                {t('requirements.downloadUploaded')}
              </a>
            )}
          </div>
        );

      case 'date':
        return (
          <div className="requirement-input-group">
            <input
              type="date"
              value={currentValue}
              onChange={(e) => handleInputChange(reqTypeId, e.target.value)}
              disabled={isDisabled}
            />
          </div>
        );

      case 'select':
        return (
          <div className="requirement-input-group">
            <select
              value={currentValue}
              onChange={(e) => handleInputChange(reqTypeId, e.target.value)}
              disabled={isDisabled}
            >
              <option value="">{t('requirements.selectOption')}</option>
              {reqType.options?.map((opt, idx) => (
                <option key={idx} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );

      default:
        return null;
    }
  };

  const renderRequirementItem = (eventReq: EventRequirement) => {
    const memberReq = getMemberRequirement(eventReq.requirementType._id);
    const status = memberReq?.status;
    const canSubmit = !status || status === 'pending' || status === 'rejected';
    const reqTypeId = eventReq.requirementType._id;
    const submitError = submitErrors[reqTypeId];

    return (
      <div key={eventReq._id} className="requirement-item">
        <div className="requirement-header">
          <div className="requirement-info">
            <span className="requirement-name">
              {eventReq.requirementType.name}
              {eventReq.isRequired && <span className="required-badge">*</span>}
            </span>
            {eventReq.requirementType.description && (
              <span className="requirement-description">{eventReq.requirementType.description}</span>
            )}
            {eventReq.dueDate && (
              <span className="requirement-due-date">
                {t('requirements.dueBy')}: {new Date(eventReq.dueDate).toLocaleDateString()}
              </span>
            )}
          </div>
          <span className={`requirement-status ${getStatusBadgeClass(status)}`}>
            {status ? t(`requirements.status.${status}`) : t('requirements.status.pending')}
          </span>
        </div>

        <div className="requirement-body">
          {renderInputField(eventReq, memberReq)}

          {submitError && (
            <div className="requirement-error">
              {submitError}
            </div>
          )}

          {memberReq?.notes && status === 'rejected' && (
            <div className="rejection-notes">
              <strong>{t('requirements.rejectionReason')}:</strong> {memberReq.notes}
            </div>
          )}

          {canSubmit && (
            <div className="requirement-actions">
              <button
                type="button"
                className="submit-btn"
                onClick={() => handleSubmit(reqTypeId)}
                disabled={submitting === reqTypeId}
              >
                {submitting === reqTypeId
                  ? t('common.saving')
                  : status === 'rejected'
                    ? t('requirements.resubmit')
                    : t('requirements.submit')}
              </button>
            </div>
          )}

          {memberReq?.reviewedBy && memberReq.reviewedAt && (
            <div className="review-info">
              {t('requirements.reviewedBy')} {memberReq.reviewedBy.name}
              {' '}{t('requirements.on')} {new Date(memberReq.reviewedAt).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="member-requirements-loading">{t('common.loading')}</div>;
  }

  if (loadError) {
    return <div className="member-requirements-error">{loadError}</div>;
  }

  if (eventRequirements.length === 0) {
    return (
      <div className="member-requirements-empty">
        <p>{t('requirements.noRequirementsConfigured')}</p>
      </div>
    );
  }

  return (
    <div className="member-event-requirements">
      {memberName && (
        <div className="member-context">
          <strong>{t('eventDetails.member')}:</strong> {memberName}
        </div>
      )}

      <div className="requirements-list">
        {eventRequirements.map(renderRequirementItem)}
      </div>
    </div>
  );
};

export default MemberEventRequirements;
